"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Language, Direction, LANGUAGES, getLanguageByCode } from "@/i18n/config";

// استيراد ملفات الترجمة
import en from "../i18n/locales/en.json"; 
import ar from "../i18n/locales/ar.json"; 
import it from "../i18n/locales/it.json"; 

const translations: Record<Language, any> = { en, ar, it };

interface LanguageContextType {
  language: Language;
  direction: Direction;
  t: (key: string, options?: { returnObjects?: boolean }) => any;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// دالة مساعدة مطورة للوصول للقيم المتداخلة ودعم المصفوفات
function getNestedValue(obj: any, path: string): any {
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = current[key];
    } else {
      return path; 
    }
  }
  return current ?? path;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");

  // الحصول على الإعدادات الحالية من ملف الـ config
  const currentLangConfig = useMemo(() => getLanguageByCode(language), [language]);
  const direction = currentLangConfig.dir;

  // تحديث سمات HTML لضمان عمل الـ CSS بشكل صحيح (مثل الخطوط والاتجاهات)
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
  }, [language, direction]);

  // دالة الترجمة تدعم الآن المصفوفات (مهم لـ ProductsSection)
  const t = useCallback(
    (key: string, options?: { returnObjects?: boolean }): any => {
      const value = getNestedValue(translations[language], key);
      return value;
    },
    [language]
  );

  // منطق التبديل الدوري الديناميكي (يعتمد على ترتيب اللغات في config.ts)
  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => {
      const currentIndex = LANGUAGES.findIndex(l => l.code === prev);
      const nextIndex = (currentIndex + 1) % LANGUAGES.length;
      return LANGUAGES[nextIndex].code;
    });
  }, []);

  return (
    <LanguageContext.Provider data-component-id="src/context/LanguageContext.tsx:69:4" data-component-path="src/context/LanguageContext.tsx" data-component-line="69" data-component-end-line="81" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" 
      value={{ 
        language, 
        direction, 
        t, 
        toggleLanguage, 
        setLanguage: setLanguageState 
      }}
    >
      <div data-component-id="src/context/LanguageContext.tsx:78:6" data-component-path="src/context/LanguageContext.tsx" data-component-line="78" data-component-end-line="80" data-component-file="LanguageContext.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%22%7D" dir={direction} className="min-h-screen">
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};